import { NO, UITextView, UITooltip, UIView, YES } from "uicore-ts"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { formatRHDateTimeWithSecondsValue } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHColors } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHNotificationDetails } from "../SocketClientFunctions"

interface NotificationRecipientRow {
    companyName: string
    email: string
    name: string
    readStatusText: string
    readDateText: string
}

export class NotificationDetailsView extends UIView {
    static readonly _readingExplanationIconMarkup =
        '<span data-notification-reading-info role="img" aria-label="Lugemise selgitus">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" ' +
        `fill="none" style="color:${RHColors.primaryBlue.stringValue};vertical-align:middle;pointer-events:auto;cursor:help" ` +
        'aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M8 7.25V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '<circle cx="8" cy="4.75" r="0.85" fill="currentColor"/>' +
        '</svg></span>'


    readonly titleLabel = new UITextView(undefined, UITextView.type.header4).configuredWithObject({
        text: "Teatise detailid"
    }).addedAsSubviewToView(this)
    readonly metadataLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, nativeSelectionEnabled: YES
    }).addedAsSubviewToView(this)
    readonly contentLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, nativeSelectionEnabled: YES
    }).addedAsSubviewToView(this)
    readonly openProcurementButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Ava" }, variant: "light",
        contentPadding: RHButtons.compactContentPadding, style: { fontSize: RHButtons.compactFontSize }
    })
    readonly recipientsLabel = new UITextView(undefined, UITextView.type.header5).configuredWithObject({
        text: "Saajad"
    }).addedAsSubviewToView(this)
    readonly recipientsView = new RHDataView<NotificationRecipientRow>().configuredWithObject({
        noDataMessage: "-", maximumIntrinsicContentHeight: 400,
        descriptors: [
            CBDataViewCellDescriptorWithObject<NotificationRecipientRow>({ keyPath: "companyName", title: "Ettevõte", allowSorting: NO }),
            CBDataViewCellDescriptorWithObject<NotificationRecipientRow>({ keyPath: "email", title: "E-post", allowSorting: NO }),
            CBDataViewCellDescriptorWithObject<NotificationRecipientRow>({ keyPath: "name", title: "Nimi", allowSorting: NO }),
            CBDataViewCellDescriptorWithObject<NotificationRecipientRow>({ keyPath: "readStatusText", title: "Loetud " + NotificationDetailsView._readingExplanationIconMarkup, allowSorting: NO }),
            CBDataViewCellDescriptorWithObject<NotificationRecipientRow>({ keyPath: "readDateText", title: "Esimene lugemine", allowSorting: NO })
        ]
    }).addedAsSubviewToView(this)

    constructor(elementID?: string) {
        super(elementID)
        const readStatusHeaderCell = this.recipientsView.tableHeaderView.cells[3]
        const readingExplanationIcon = new UIView(undefined,
            readStatusHeaderCell.viewHTMLElement.querySelector<HTMLElement>("[data-notification-reading-info]"))
            .configuredWithObject({ style: { pointerEvents: "none" } })
        UITooltip.sharedInstance.attach(readingExplanationIcon,
            "Teatis loetakse loetuks, kui saaja avab sisselogituna keskkonnas selle teatise või teatisega seotud sisu. " +
            "Salvestatakse esimese lugemise kuupäev ja kellaaeg. Üksnes e-kirja või selle lingi avamine ei loe. " +
            "Kriips tähendab, et lugemise kohta ei ole usaldusväärseid andmeid või " +
            "saajal ei olnud teate saatmisel kasutajakontot.")
    }

    applyDetailsWithFallbackSummaryHTML(details: RHNotificationDetails, fallbackSummaryHTML: string) {
        const escapedHTML = RHTextSanitizer.escapedHTML
        let procurementText = "-"
        if (details.procurement) {
            procurementText = escapedHTML(details.procurement.title)
            if (details.procurement.referenceNumber) procurementText += " (" + escapedHTML(details.procurement.referenceNumber) + ")"
        }
        let wasEmailSentText = "ei"
        if (details.emailStatus === "sent") wasEmailSentText = "jah"
        this.metadataLabel.text = `<b>Hange:</b> ${procurementText}<br><b>Teatise aeg:</b> ${formatRHDateTimeWithSecondsValue(details.creationDate)}<br><b>Kas e-kiri on saadetud?</b> ${wasEmailSentText}`
        let contentHTML = fallbackSummaryHTML
        if (details.content !== undefined) contentHTML = escapedHTML(details.content).replace(/\n/g, "<br>")
        this.contentLabel.text = "<b>Teate sisu</b><br>" + contentHTML
        if (details.procurement) this.openProcurementButton.addedAsSubviewToView(this)
        else this.openProcurementButton.removeFromSuperview()
        this.recipientsView.data = details.recipients.map(recipient => {
            let readStatusText = "-"
            if (recipient.readStatus === "read") readStatusText = "Jah"
            if (recipient.readStatus === "unread") readStatusText = "Ei"
            let readDateText = "-"
            if (recipient.readDate) readDateText = formatRHDateTimeWithSecondsValue(recipient.readDate)
            return {
                companyName: recipient.companyName ?? "-",
                email: recipient.email ?? "-",
                name: recipient.name ?? "-",
                readStatusText,
                readDateText
            }
        })
        this.setNeedsLayoutUpToRootView()
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)
        bounds.rectangleWithHeight(this.titleLabel).assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding, this.metadataLabel).assignedAsFrameOfView(this.metadataLabel)
            .rectangleForNextRow(padding, this.contentLabel).assignedAsFrameOfView(this.contentLabel)
            .IF(this.openProcurementButton.superview === this)
            .rectangleForNextRow(padding, 28)
            .rectangleWithWidth(72).assignedAsFrameOfView(this.openProcurementButton)
            .rectangleWithWidth(bounds.width)
            .ENDIF()
            .rectangleForNextRow(padding, this.recipientsLabel).assignedAsFrameOfView(this.recipientsLabel)
            .rectangleForNextRow(padding, this.recipientsView).assignedAsFrameOfView(this.recipientsView)
    }
}
